// Visit The Stimulus Handbook for more details
// https://stimulusjs.org/handbook/introduction
//
// This example controller works with specially annotated HTML like:
//
// <div data-controller="hello">
//   <h1 data-target="hello.output"></h1>
// </div>

import Utility from '../classes/Utility';
import { Controller } from "stimulus"
import axios from 'axios';
import Events from '../classes/Events';
import Constants from '../classes/Constants';

const codeToTheme = code => {
  let theme = 'default';
  let caseInsensitiveCode = code;
  if (code) {
    caseInsensitiveCode = code.toUpperCase();
  }
  Object.keys(Constants.couponThemes).some(themeKey => {
    const { regex } = Constants.couponThemes[themeKey];
    if (regex && regex.test(caseInsensitiveCode)) {
      theme = themeKey;
      return true;
    }
    return false;
  })
  return theme;
}

const InvalidCouponMessage = 'Invalid Coupon Code';

export default class extends Controller {
  static targets = [
    'couponCode',
    'finalPrice',
    'originalPrice',
    'couponOverview',
    'couponInput',
    'appliedCoupon',
    'enterCoupon',
    'form',
    'illustration',
    'errorMessage'
  ]

  async submit(e) {
    e && e.preventDefault();
    this.discount = await this.retrieveCoupon(this.coupon);
    if (this.discount) {
      this.clearError();
      this.formTarget.dispatchEvent(Events.submittedEvent);
    } else {
      this.showError();
    }
  }

  async retrieveCoupon(couponCode) {
    if (couponCode.indexOf(' ') !== -1) {
      return false;
    }
    const res = await axios({
      method: 'get',
      url: `/api/coupon_validator/${couponCode}/`,
      dataType: 'json'
    })
    const { data } = res;
    if (data.result) {
      return data.data.amount_off;
    } else {
      return false;
    }
  }

  clearError() {
    this.couponCodeTarget.classList.add('t-border-yellow-500')
    this.couponCodeTarget.classList.remove('t-border-red-700')
    this.errorMessageTarget.classList.add('t-hidden');
  }

  showError() {
    this.couponCodeTarget.classList.remove('t-border-yellow-500')
    this.couponCodeTarget.classList.add('t-border-red-700')
    this.errorMessageTarget.textContent = InvalidCouponMessage;
    this.errorMessageTarget.classList.remove('t-hidden');
    Utility.setLocalCookies('coupon_code', 'unknown');
  }

  reset(e) {
    e && e.preventDefault();
    this.couponOverviewTarget.classList.remove('t-hidden');
    this.enterCouponTarget.classList.remove('t-hidden');
    this.appliedCouponTarget.classList.add('t-hidden');
    this.couponInputTarget.classList.add('t-hidden');
    this.originalPriceTarget.classList.add('t-opacity-0');
    this.finalPriceTarget.textContent = '$199.00'
    this.updateTheme({reset: true});
    this.clearError();
    Utility.setLocalCookies('coupon_code', 'unknown');
    this.coupon = null;
    this.couponCodeTarget.value = "";
    this.formTarget.setAttribute('data-subscription-price', this.originalPrice.toFixed(2))
  }

  handleCouponInput(e) {
    const { target } = e;
    this.coupon = target.value;
  }

  toggleCouponInputState(e) {
    e && e.preventDefault();
    this.couponOverviewTarget.classList.toggle('t-hidden')
    this.couponInputTarget.classList.toggle('t-hidden')
  }

  updateCoupon() {
    // 1. update price
    this.updatePrice()
    // 2. show Button of when Coupon is applied
    this.enterCouponTarget.classList.add('t-hidden')
    this.appliedCouponTarget.childNodes[0].textContent = `${this.coupon} (-$${this.discount})`;
    this.appliedCouponTarget.classList.remove('t-hidden')
    // 3. toggle back to main coupon view
    this.toggleCouponInputState()
    // 3. update cookie
    Utility.setLocalCookies('coupon_code', this.coupon);
  }

  updateTheme(config) {
    const reset = config && config.reset === true;
    this.removeTheme();
    this.theme = reset ? Constants.couponThemes['default'] : Constants.couponThemes[codeToTheme(this.coupon) || 'default'];
    this.addTheme();
  }

  addTheme() {
    if (this.theme) {
      const {
        borderColor,
        background,
        highlightColor,
        illustration
      } = this.theme

      const addStyle = (element, property, styling) => {
        const { style, class: className } = styling;
        if (style) {
          // clearing style on that property
          element.style.setProperty(property, style);
        } else {
          element.classList.add(className);
        }
      }
      // Container border
      addStyle(this.element, 'border-color', borderColor);
      // Dashed divider
      addStyle(this.couponOverviewTarget, 'border-color', borderColor);
      // Container background
      addStyle(this.element, 'background-image', background);
      // Coupon buttons
      addStyle(this.enterCouponTarget, 'background-color', highlightColor);
      addStyle(this.appliedCouponTarget, 'background-color', highlightColor);
      // Coupon input background
      addStyle(this.couponInputTarget, 'background-color', highlightColor);
      // Coupon input border
      addStyle(this.couponCodeTarget, 'border-color', borderColor);
      // Illustration
      this.illustrationTarget.src = illustration.src;
    }
  }

  removeTheme() {
    if (this.theme) {
      const {
        borderColor,
        background,
        highlightColor
      } = this.theme

      const revertStyle = (element, property, styling) => {
        const { style, class: className } = styling;
        if (style) {
          // clearing style on that property
          element.style.setProperty(property, '');
        } else {
          element.classList.remove(className);
        }
      }
      // Container border
      revertStyle(this.element, 'border-color', borderColor);
      // Dashed divider
      revertStyle(this.couponOverviewTarget, 'border-color', borderColor);
      // Container background
      revertStyle(this.element, 'background-image', background);
      // Coupon buttons
      revertStyle(this.enterCouponTarget, 'background-color', highlightColor);
      revertStyle(this.appliedCouponTarget, 'background-color', highlightColor);
      // Coupon input background
      revertStyle(this.couponInputTarget, 'background-color', highlightColor);
      // Coupon input border
      revertStyle(this.couponCodeTarget, 'border-color', borderColor);
    }
  }

  updatePrice() {
    this.originalPriceTarget.classList.remove('t-opacity-0');
    this.finalPriceTarget.textContent = `$${(this.originalPrice - this.discount).toFixed(2)}`;
    this.formTarget.setAttribute('data-subscription-price', (this.originalPrice - this.discount).toFixed(2))
  }

  connect() {
    EventManager.on('payment-form-loaded', () => {
      this.element.classList.remove('t-hidden');
    })

    this.theme = Constants.couponThemes['default'];
    this.updateTheme();
    const params = new URLSearchParams(window.location.search);

    // Remember original price
    this.originalPrice = parseFloat(this.formTarget.dataset['originalPrice'])
    this.formTarget.setAttribute('data-subscription-price', this.originalPrice.toFixed(2))

    // Retrieves coupon from url param or
    // cookie value
    if (params.has('coupon')) {
      Utility.setLocalCookies('coupon_code', params.get('coupon'));
      this.coupon = params.get('coupon');
    } else {
      this.coupon = Utility.getCookie('coupon_code');
    }

    // If there is an existing coupon,
    // Automatically go through the coupon input flow
    if (this.coupon != 'unknown') {
      this.toggleCouponInputState();
      this.couponCodeTarget.value = this.coupon;
      this.submit();
    }

    EventManager.on('force-reset-coupon', () => {
      this.reset();
    })
  }
}
